import { I18n } from 'common/i18n';
import { type ViewColumn } from 'common/types/viewColumn';
import { useCallback } from 'react';
import {
  type TableExportType,
  type ExportData,
  type ExportTotalsInfo,
  type TableColumnFormat,
} from '../types';
import { type AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import { hideToastById } from 'common/components/ToastNotification/Toastmaster';
import { NULL_GROUPING } from '../Constants';
import { formatGroupLabelValue } from '../helpers/formatGroupLabelValue';
import { processCellCallback } from '../helpers/processCellCallback';
import { isDuplicateGroupRow } from '../helpers/isDuplicateGroupRow';
import { isSubtotalCell } from '../helpers/isTotalCell';
import { type Hierarchy, type HierarchyColumnConfig } from 'common/visualizations/vif';
import { isTableAdvancedTotalingForExportsEnabled } from 'common/visualizations/helpers/VifSelectors';

export interface IInitializeExportCallback {
  selectedFiltered: boolean;
}

interface IUseInitializeExport {
  viewColumns: ViewColumn[];
  getExportData:
    | ((selectedFiltered: boolean, exportType: TableExportType) => Promise<ExportData>)
    | undefined;
  setIsExportTableRendered: React.Dispatch<React.SetStateAction<boolean>>;
  setRowsToBeExported: React.Dispatch<React.SetStateAction<unknown[] | undefined>>;
  setExportTotalsInfo: React.Dispatch<React.SetStateAction<ExportTotalsInfo | undefined>>;
}

/**
 *
 * @param viewColumns Visible columns in the rendered AgGrid table
 * @param getExportData A method that fetches the data that is needed to be exported
 * @param setIsExportTableRendered A `setState` method that handles the condition for determining if the export table has rendered
 * @param setRowsToBeExported A `setState` method that handles the rows of the rendered export table
 * @returns The method to initialize the export table
 */
export const useInitializeExport = ({
  viewColumns,
  getExportData,
  setIsExportTableRendered,
  setRowsToBeExported,
  setExportTotalsInfo,
}: IUseInitializeExport) => {
  return useCallback(
    (data: IInitializeExportCallback) => {
      if (viewColumns.length > 0 && getExportData) {
        // clear stale totals from previous exports before fetching new data
        setExportTotalsInfo(undefined);
        getExportData(data.selectedFiltered, 'XLSX')
          ?.then((exportData) => {
            setIsExportTableRendered(true);
            setRowsToBeExported(exportData.rows);
            if (exportData.subtotalLookup && exportData.levelMetadata) {
              setExportTotalsInfo({
                subtotalLookup: exportData.subtotalLookup,
                levelMetadata: exportData.levelMetadata,
                grandTotals: exportData.grandTotals || null,
              });
            }
          })
          .catch((error) => {
            console.error('Failed to fetch export data:', error);
            // Reset states on error
            setIsExportTableRendered(false);
            setRowsToBeExported(undefined);
            setExportTotalsInfo(undefined);
          });
      }
    },
    [viewColumns, getExportData, setIsExportTableRendered, setRowsToBeExported, setExportTotalsInfo],
  );
};

interface IUseHandleExport {
  gridRef: React.RefObject<AgGridReact<unknown>>;
  isExportTableRendered: boolean;
  rowsToBeExported: unknown[] | undefined;
  datasetName: string | undefined;
  setIsExportTableRendered: React.Dispatch<React.SetStateAction<boolean>>;
  setRowsToBeExported: React.Dispatch<React.SetStateAction<unknown[] | undefined>>;
  exportTotalsInfo: ExportTotalsInfo | undefined;
  setExportTotalsInfo: React.Dispatch<React.SetStateAction<ExportTotalsInfo | undefined>>;
  viewColumns: ViewColumn[];
  columnFormats: { [key: string]: TableColumnFormat };
  domain: string;
  datasetUid: string;
}

/*
 Advanced totaling specific feature to generate custom subtotal label for exported tables.
 Requires: enable_table_advanced_totaling_for_exports = true and enable_table_advanced_totaling = true;
*/
const generateCustomSubtotalLabel = (hierarchyConfig: Hierarchy, nodeField: string) => {
  let subtotalLabel = I18n.t('shared.visualizations.charts.table.subtotal');
  const columnConfigurations = hierarchyConfig?.columnConfigurations;
  const columnConfig = columnConfigurations?.find((columnConfiguration: HierarchyColumnConfig) => {
    return columnConfiguration.columnName === nodeField;
  });
  const globalSubtotalLabel = hierarchyConfig?.customLabels?.subtotals?.trim();
  if (globalSubtotalLabel) {
    subtotalLabel = globalSubtotalLabel;
  }
  const columnSubtotalLabel = columnConfig?.subtotalLabel?.trim();
  if (columnSubtotalLabel) {
    subtotalLabel = columnSubtotalLabel;
  }
  return subtotalLabel;
};

/**
 *
 * @param gridRef The reference object to the rendered export table
 * @param datasetName The name of the dataset to be set as part of the filename of the exported XLSX file
 * @param isExportTableRendered The condition for determining if the export table has rendered
 * @param setIsExportTableRendered A `setState` method that handles the condition for determining if the export table has rendered
 * @param rowsToBeExported The rows of the rendered export table
 * @param setRowsToBeExported A `setState` method that handles the rows of the rendered export table
 * @param vifOrderConfig Contains the sorting configuration of the table
 * @returns The actual method that exports the rendered table
 */
export const useHandleExport = ({
  gridRef,
  datasetName,
  isExportTableRendered,
  rowsToBeExported,
  setIsExportTableRendered,
  setRowsToBeExported,
  exportTotalsInfo,
  setExportTotalsInfo,
  viewColumns,
  columnFormats,
  domain,
  datasetUid,
}: IUseHandleExport) => {
  return useCallback(() => {
    if (isExportTableRendered && gridRef.current && rowsToBeExported !== undefined) {
      const exportGridApi = gridRef.current.api;
      const currentDate = moment().format('YYYYMMDD');
      const fileName = `${datasetName}_${currentDate}`;

      exportGridApi.exportDataAsExcel({
        fileName: fileName,
        shouldRowBeSkipped: ({ node, context }) =>
          (context.hideDuplicateGroups ?? false) && isDuplicateGroupRow(node),
        processCellCallback: (params) => {
          return processCellCallback(params, exportTotalsInfo);
        },
        processRowGroupCallback(params) {
          const { node } = params;
          const noValueText = I18n.t('shared.visualizations.charts.common.summary_table.no_value');
          const value = node.key === NULL_GROUPING ? noValueText : node.key;
          // Grouping value
          if (!node.footer) {
            return value;
          }
          // the subtotal/total label concatenates the group value into a string, so excel's
          // cell-level number format no longer applies. format the value here so the label
          // matches the group header (e.g. `Subtotal 2,012`, not `Subtotal 2012`).
          const labelValue = formatGroupLabelValue({
            key: node.key,
            fieldName: node.field,
            viewColumns,
            columnFormats,
            domain,
            datasetUid,
          });
          if (isSubtotalCell(params)) {
            if (isTableAdvancedTotalingForExportsEnabled()) {
              const customSubtotalLabel = generateCustomSubtotalLabel(
                params.context.hierarchyConfig,
                params.node.field || '',
              );
              return `${customSubtotalLabel} ${labelValue}`;
            }
            return `${I18n.t('shared.visualizations.charts.table.subtotal')} ${labelValue}`;
          }
          // root-level footer (grand total)
          if (isTableAdvancedTotalingForExportsEnabled()) {
            const customTotalLabel = params.context.hierarchyConfig?.customLabels?.totals?.trim();
            if (customTotalLabel) {
              return customTotalLabel;
            }
          }
          return I18n.t('shared.visualizations.charts.table.total');
        },
      });

      hideToastById('export-modal-toast');
      setIsExportTableRendered(false);
      setRowsToBeExported(undefined);
      setExportTotalsInfo(undefined);
    }
  }, [
    datasetName,
    gridRef,
    isExportTableRendered,
    rowsToBeExported,
    setIsExportTableRendered,
    setRowsToBeExported,
    exportTotalsInfo,
    setExportTotalsInfo,
    viewColumns,
    columnFormats,
    domain,
    datasetUid,
  ]);
};
